/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Button, Select, Card, Row, Col, message } from 'antd';
import { Input } from 'antd';
import { imagingOrderService, imagingStudyService, imagingDepartmentService } from '../../../services/imagingService';
import { patientService } from '../../../services/patientService';
import { doctorService } from '../../../services/doctorService';
import Breadcrumb from '../../../components/ui/Breadcrumb';

const { TextArea } = Input;

const AddImagingOrder = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const [departments, setDepartments] = useState([]);
  const [studies, setStudies] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [patients, setPatients] = useState([]);
  const [patientSearching, setPatientSearching] = useState(false);
  const [studiesLoading, setStudiesLoading] = useState(false);

  const fetchDepartments = useCallback(async () => {
    try {
      const res = await imagingDepartmentService.getAll({ is_active: true, per_page: 100 });
      setDepartments(res.data || []);
    } catch {
      message.error('Error al cargar departamentos');
    }
  }, []);

  const fetchDoctors = useCallback(async () => {
    try {
      const res = await doctorService.getAll(100);
      setDoctors(res.data || []);
    } catch {
      message.error('Error al cargar médicos');
    }
  }, []);

  useEffect(() => {
    fetchDepartments();
    fetchDoctors();
  }, [fetchDepartments, fetchDoctors]);

  const handleDepartmentChange = async (departmentId) => {
    form.setFieldValue('study_id', undefined);
    setStudies([]);
    if (!departmentId) return;

    setStudiesLoading(true);
    try {
      const res = await imagingStudyService.getAll({ department_id: departmentId, is_active: true, per_page: 100 });
      setStudies(res.data || []);
    } catch {
      message.error('Error al cargar estudios');
    } finally {
      setStudiesLoading(false);
    }
  };

  const handlePatientSearch = async (term) => {
    if (!term || term.length < 2) return;
    setPatientSearching(true);
    try {
      const results = await patientService.search(term);
      setPatients(results || []);
    } catch {
      setPatients([]);
    } finally {
      setPatientSearching(false);
    }
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      await imagingOrderService.create(values);
      message.success('Orden de imagen creada correctamente');
      navigate('/imaging');
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Error al crear la orden';
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="page-header">
        <div className="row align-items-center">
          <div className="col">
            <h3 className="page-title">Nueva Orden de Imagen</h3>
            <Breadcrumb items={[{ label: 'Inicio', to: '/' }, { label: 'Órdenes de Imagen', to: '/imaging' }, { label: 'Nueva Orden' }]} />
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-md-12">
          <Card title="Nueva Orden de Imagen">
            <Form form={form} layout="vertical" onFinish={onFinish}>
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Paciente"
                    name="patient_id"
                    rules={[{ required: true, message: 'El paciente es requerido' }]}
                  >
                    <Select
                      showSearch
                      placeholder="Buscar paciente por nombre o cédula..."
                      filterOption={false}
                      onSearch={handlePatientSearch}
                      loading={patientSearching}
                      notFoundContent={patientSearching ? 'Buscando...' : 'Sin resultados'}
                    >
                      {patients.map(p => (
                        <Select.Option key={p.id} value={p.id}>
                          {p.first_name} {p.last_name}
                          {p.identification ? ` — ${p.identification}` : ''}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    label="Médico Solicitante"
                    name="doctor_id"
                    rules={[{ required: true, message: 'El médico es requerido' }]}
                  >
                    <Select
                      showSearch
                      placeholder="Seleccione médico"
                      optionFilterProp="children"
                    >
                      {doctors.map(d => (
                        <Select.Option key={d.id} value={d.id}>
                          {d.first_name} {d.last_name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Departamento"
                    name="department_id"
                    rules={[{ required: true, message: 'Seleccione un departamento' }]}
                  >
                    <Select
                      placeholder="Seleccione departamento"
                      onChange={handleDepartmentChange}
                      allowClear
                    >
                      {departments.map(dep => (
                        <Select.Option key={dep.id} value={dep.id}>
                          {dep.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    label="Estudio"
                    name="study_id"
                    rules={[{ required: true, message: 'El estudio es requerido' }]}
                  >
                    <Select
                      placeholder={studies.length === 0 ? 'Seleccione un departamento primero' : 'Seleccione estudio'}
                      loading={studiesLoading}
                      disabled={studies.length === 0}
                      showSearch
                      optionFilterProp="children"
                    >
                      {studies.map(s => (
                        <Select.Option key={s.id} value={s.id}>
                          {s.name}
                          {s.requires_contrast ? ' (con contraste)' : ''}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item label="Indicación Clínica" name="clinical_indication">
                <TextArea rows={3} placeholder="Motivo clínico de la orden..." />
              </Form.Item>

              <Form.Item label="Observaciones" name="observations">
                <TextArea rows={2} placeholder="Observaciones adicionales..." />
              </Form.Item>

              <Form.Item className="text-end">
                <Button onClick={() => navigate('/imaging')} className="me-2">
                  Cancelar
                </Button>
                <Button type="primary" htmlType="submit" loading={loading}>
                  Crear Orden
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </div>
      </div>
    </>
  );
};

export default AddImagingOrder;
